import type { Chain } from '../../chain'
import {
  type InternalTransaction,
  type LowerHex,
  toLowerHex,
} from '../../types'
import type { TransactionDetail } from '../../types/transactionDetail'
import { decodeTokenTransferLogs } from './decodeTokenTransferLogs'

type GetTransactionDetailParameters = {
  chain: Chain
  hash: LowerHex
  timestamp?: number
  internalTransactions?: readonly InternalTransaction[]
  timeout?: number
  input?: LowerHex
  value?: bigint
}

export const getTransactionDetail = async ({
  chain,
  hash,
  timestamp: optionalTimestamp,
  internalTransactions = [],
  timeout,
  input,
  value,
}: GetTransactionDetailParameters): Promise<TransactionDetail> => {
  const [transaction, receipt] = await Promise.all([
    input === undefined || value === undefined
      ? chain.client.call({
          clientCall: (client) => client.getTransaction({ hash }),
          callName: 'getTransaction',
          timeout,
        })
      : {
          input,
          value,
        },
    chain.client.call({
      clientCall: (client) => client.getTransactionReceipt({ hash }),
      callName: 'getTransactionReceipt',
      timeout,
    }),
  ])
  const timestamp =
    optionalTimestamp ??
    (await getTimestampOfBlockNumber(chain, receipt.blockNumber, timeout))

  const {
    erc20Transfers,
    erc721Transfers,
    erc1155Transfers,
    inferredNativeTransfers,
    logs,
  } = decodeTokenTransferLogs(
    receipt.logs.map((log) => ({
      address: toLowerHex(log.address),
      data: toLowerHex(log.data),
      logIndex: log.logIndex,
      topics: log.topics.map((topic) => toLowerHex(topic)),
      transactionHash: toLowerHex(log.transactionHash),
    })),
    chain.wrappedTokenAddresses,
  )
  const mergedInternalTransactions = mergeInternalTransactions({
    internalTransactions,
    inferredNativeTransfers,
  })

  return {
    blockNumber: Number(receipt.blockNumber),
    chainId: chain.id,
    contractAddress:
      receipt.contractAddress !== undefined && receipt.contractAddress !== null
        ? toLowerHex(receipt.contractAddress)
        : undefined,
    effectiveGasPrice: receipt.effectiveGasPrice,
    erc20Transfers,
    erc721Transfers,
    erc1155Transfers,
    from: toLowerHex(receipt.from),
    gasUsed: receipt.gasUsed,
    hash: toLowerHex(receipt.transactionHash),
    input: toLowerHex(transaction.input),
    internalTransactions: mergedInternalTransactions,
    logs,
    status: receipt.status,
    timestamp,
    to: receipt.to === null ? undefined : toLowerHex(receipt.to),
    value: transaction.value,
  }
}

/**
 * 取得済み internal transaction とログから補完した native transfer を統合する。
 *
 * @param parameters.internalTransactions - Explorer などから取得済みの internal transaction
 * @param parameters.inferredNativeTransfers - ログから補完した native transfer
 * @returns 同一 transfer の件数を維持して統合した internal transaction
 */
export const mergeInternalTransactions = ({
  internalTransactions,
  inferredNativeTransfers,
}: {
  internalTransactions: readonly InternalTransaction[]
  inferredNativeTransfers: readonly InternalTransaction[]
}): InternalTransaction[] => {
  const mergedInternalTransactions = [...internalTransactions]
  const existingTransferCounts = new Map<string, number>()
  for (const internalTransaction of internalTransactions) {
    if (internalTransaction.isError) {
      continue
    }
    const key = createInternalTransactionKey({ internalTransaction })
    existingTransferCounts.set(key, (existingTransferCounts.get(key) ?? 0) + 1)
  }

  for (const inferredNativeTransfer of inferredNativeTransfers) {
    const key = createInternalTransactionKey({
      internalTransaction: inferredNativeTransfer,
    })
    const existingTransferCount = existingTransferCounts.get(key) ?? 0
    if (existingTransferCount > 0) {
      existingTransferCounts.set(key, existingTransferCount - 1)
      continue
    }
    mergedInternalTransactions.push(inferredNativeTransfer)
  }

  return mergedInternalTransactions
}

/**
 * internal transaction の内容を重複判定用のキーに変換する。
 *
 * @param parameters.internalTransaction - キーへ変換する internal transaction
 * @returns transaction hash、送受信者、数量を連結したキー
 */
const createInternalTransactionKey = ({
  internalTransaction,
}: {
  internalTransaction: InternalTransaction
}): string =>
  [
    internalTransaction.txHash,
    internalTransaction.from,
    internalTransaction.to ?? '',
    internalTransaction.value.toString(),
  ].join('/')

const getTimestampOfBlockNumber = async (
  chain: Chain,
  blockNumber: bigint,
  timeout?: number,
): Promise<number> => {
  const block = await chain.client.call({
    clientCall: (client) => client.getBlock({ blockNumber }),
    callName: 'getBlock',
    timeout,
  })
  return Number(block.timestamp) * 1000
}
